/**
 * SuperAdmin — AI Cost Monitor (Phase 10.SA3)
 * Period selector (7d/30d/90d), per-tenant table with alert threshold config.
 *
 * Data: GET /api/superadmin/ai-costs?period=7d|30d|90d
 *       POST /api/superadmin/ai-costs/alerts
 * Web only — never add to mobile RoleRouter (ADR-014: finance screens web-only).
 * ConfirmModal required before every POST /alerts (CONSTRAINT 13 pattern).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FiLoader, FiRefreshCw } from 'react-icons/fi';
import { getAiCosts, setAiCostAlert } from '../../utils/api';

const PERIODS = [
  { value: '7d',  label: '7 Days'  },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
];

const PLAN_BADGE = {
  AI_ADMIN: { bg: '#E1F5EE', color: '#085041', label: 'AI Admin' },
  SCALE:    { bg: '#E1F5EE', color: '#085041', label: 'Scale'    },
  STARTER:  { bg: '#EEEDFE', color: '#3C3489', label: 'Starter'  },
  FREE:     { bg: '#F1EFE8', color: '#5F5E5A', label: 'Free'     },
};

const STYLE_ID = 'sa-aicost-styles';
if (!document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @media (max-width: 700px) { .sa-ai-metrics { grid-template-columns: repeat(2, 1fr) !important; } }
    .sa-ai-row:hover td { background: #fafafa !important; }
    @keyframes sa-ai-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(el);
}

// ─── Inline ConfirmModal (same pattern as SoloQueue.js, OnboardingQueue.js) ─

function ConfirmModal({ visible, title, message, confirmLabel, onConfirm, onCancel }) {
  if (!visible) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 400, width: '100%',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <h3 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, color: '#1A1A2E' }}>{title}</h3>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: '#555', lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button onClick={onCancel}
            style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d4d4d4',
                     background: '#fff', fontSize: 14, cursor: 'pointer', color: '#333' }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            style={{ padding: '9px 20px', borderRadius: 8, border: 'none',
                     background: '#2563EB', color: '#fff', fontSize: 14,
                     fontWeight: 600, cursor: 'pointer' }}>
            {confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Alert Config Modal ──────────────────────────────────────────────────────

function AlertConfigModal({ target, onSaved, onClose }) {
  const [threshold,    setThreshold]    = useState(String(target?.alert?.threshold ?? 5000));
  const [alertEmail,   setAlertEmail]   = useState(target?.alert?.alertEmail ?? '');
  const [enabled,      setEnabled]      = useState(target?.alert?.enabled ?? false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error,        setError]        = useState('');

  async function handleConfirm() {
    setShowConfirm(false);
    setIsSubmitting(true);
    try {
      await setAiCostAlert({
        tenantId:   target.tenantId,
        threshold:  Number(threshold),
        alertEmail: alertEmail.trim(),
        enabled,
      });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save alert config');
      setIsSubmitting(false);  // reset only on error
    }
  }

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 900,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 440, width: '100%',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#1A1A2E' }}>
            Configure Alert
          </h3>
          <p style={{ margin: '0 0 20px', fontSize: 13, color: '#777' }}>
            {target?.tenantName}
          </p>

          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6 }}>
            Cost Threshold (₹ INR)
          </label>
          <input
            type="number"
            min="0"
            value={threshold}
            onChange={e => setThreshold(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d4d4d4',
                     fontSize: 14, marginBottom: 16, boxSizing: 'border-box' }}
          />

          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6 }}>
            Alert Email
          </label>
          <input
            type="email"
            value={alertEmail}
            onChange={e => setAlertEmail(e.target.value)}
            placeholder="alerts@example.com"
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d4d4d4',
                     fontSize: 14, marginBottom: 16, boxSizing: 'border-box' }}
          />

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                          fontSize: 14, color: '#333', marginBottom: 20 }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
              style={{ width: 16, height: 16 }}
            />
            Enable alert notifications
          </label>

          {error && (
            <p style={{ color: '#A32D2D', fontSize: 13, marginBottom: 12 }}>{error}</p>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button onClick={onClose} disabled={isSubmitting}
              style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d4d4d4',
                       background: '#fff', fontSize: 14, cursor: 'pointer', color: '#333' }}>
              Cancel
            </button>
            <button onClick={() => setShowConfirm(true)} disabled={isSubmitting}
              style={{ padding: '9px 20px', borderRadius: 8, border: 'none',
                       background: isSubmitting ? '#ccc' : '#2563EB', color: '#fff',
                       fontSize: 14, fontWeight: 600,
                       cursor: isSubmitting ? 'not-allowed' : 'pointer',
                       display: 'flex', alignItems: 'center', gap: 8 }}>
              {isSubmitting && (
                <FiLoader style={{ animation: 'sa-ai-spin 1s linear infinite' }} size={14} />
              )}
              Save Alert
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        visible={showConfirm}
        title="Save Alert Config?"
        message={`Set cost alert for ${target?.tenantName} — threshold ₹${Number(threshold).toLocaleString('en-IN')}, ${enabled ? 'enabled' : 'disabled'}.`}
        confirmLabel="Save Alert"
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
      />
    </>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function AICostMonitor() {
  const [period,      setPeriod]      = useState('30d');
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [alertTarget, setAlertTarget] = useState(null);

  const fetchCosts = useCallback(async (p = period) => {
    setLoading(true);
    setError('');
    try {
      const res = await getAiCosts(p);
      setData(res.data.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load AI cost data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchCosts(period); }, [period]);  // eslint-disable-line react-hooks/exhaustive-deps

  function handlePeriodChange(p) {
    setPeriod(p);
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
        <FiLoader size={28} style={{ animation: 'sa-ai-spin 1s linear infinite', color: '#2563EB' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#A32D2D' }}>
        <p>{error}</p>
        <button onClick={() => fetchCosts(period)}
          style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #A32D2D',
                   background: '#fff', color: '#A32D2D', fontSize: 14, cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  const summary = data?.summary || {};
  const byCentre = data?.byCentre || [];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1A1A2E' }}>
          AI Cost Monitor
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Period selector */}
          <div style={{ display: 'flex', borderRadius: 8, border: '1px solid #d4d4d4', overflow: 'hidden' }}>
            {PERIODS.map(p => (
              <button key={p.value} onClick={() => handlePeriodChange(p.value)}
                style={{ padding: '7px 16px', border: 'none', fontSize: 13, cursor: 'pointer',
                         fontWeight: period === p.value ? 700 : 400,
                         background: period === p.value ? '#2563EB' : '#fff',
                         color: period === p.value ? '#fff' : '#333' }}>
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={() => fetchCosts(period)}
            style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #d4d4d4',
                     background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiRefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Summary metric cards */}
      <div className="sa-ai-metrics" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                                              gap: 16, marginBottom: 24 }}>
        <MetricCard
          label="Total AI Cost"
          value={`₹${Number(summary.totalCostInr || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
          sub={`Last ${period}`}
          color="#2563EB"
        />
        <MetricCard
          label="Total AI Calls"
          value={Number(summary.totalCalls || 0).toLocaleString('en-IN')}
          sub="Agent runs"
          color="#085041"
        />
        <MetricCard
          label="Centres Over Threshold"
          value={summary.centresOver || 0}
          sub="Alert thresholds exceeded"
          color={summary.centresOver > 0 ? '#A32D2D' : '#085041'}
        />
      </div>

      {/* Per-centre table */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {['Centre', 'Plan', 'Calls', 'Sonnet (K)', 'Haiku (K)', 'Est. Cost', 'Threshold', 'Alert'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600,
                                       color: '#555', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {byCentre.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#999' }}>
                    No AI usage in this period
                  </td>
                </tr>
              ) : byCentre.map(row => {
                const over    = row.alert?.overThreshold;
                const badge   = PLAN_BADGE[row.plan] || PLAN_BADGE.FREE;
                const alertOn = row.alert?.enabled;

                return (
                  <tr key={row.tenantId} className="sa-ai-row"
                    style={{ borderBottom: '1px solid #f0f0f0',
                             background: over ? '#FDECEA' : 'transparent' }}>
                    <td style={{ padding: '10px 14px', color: over ? '#A32D2D' : '#1A1A2E',
                                 fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {over && (
                        <span style={{ display: 'inline-block', background: '#A32D2D', color: '#fff',
                                       fontSize: 10, fontWeight: 700, padding: '1px 6px',
                                       borderRadius: 4, marginRight: 6 }}>
                          OVER
                        </span>
                      )}
                      {row.tenantName}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ display: 'inline-block', background: badge.bg, color: badge.color,
                                     fontSize: 11, fontWeight: 600, padding: '2px 8px',
                                     borderRadius: 6, whiteSpace: 'nowrap' }}>
                        {badge.label}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: over ? '#A32D2D' : '#333' }}>
                      {(row.totalCalls || 0).toLocaleString('en-IN')}
                    </td>
                    <td style={{ padding: '10px 14px', color: over ? '#A32D2D' : '#333' }}>
                      {row.units?.sonnetTokens1K ?? '—'}
                    </td>
                    <td style={{ padding: '10px 14px', color: over ? '#A32D2D' : '#333' }}>
                      {row.units?.haikuTokens1K ?? '—'}
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 600,
                                 color: over ? '#A32D2D' : '#1A1A2E' }}>
                      ₹{Number(row.costs?.total || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#555' }}>
                      ₹{Number(row.alert?.threshold || 5000).toLocaleString('en-IN')}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <button
                        onClick={() => setAlertTarget(row)}
                        style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                                 cursor: 'pointer', whiteSpace: 'nowrap',
                                 border: alertOn ? '1px solid #16A34A' : '1px solid #d4d4d4',
                                 background: alertOn ? '#F0FDF4' : '#fff',
                                 color: alertOn ? '#16A34A' : '#555' }}>
                        {alertOn ? 'Alert On' : 'Set Alert'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alert config modal */}
      {alertTarget && (
        <AlertConfigModal
          target={alertTarget}
          onSaved={() => { setAlertTarget(null); fetchCosts(period); }}
          onClose={() => setAlertTarget(null)}
        />
      )}
    </div>
  );
}

function MetricCard({ label, value, sub, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb',
                  padding: '18px 20px' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || '#1A1A2E', marginBottom: 4 }}>
        {value}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: '#999' }}>{sub}</div>
    </div>
  );
}
